import {
	PropsWithChildren,
	ReactElement,
	ReactNode,
	useMemo
} from "react";
import styles from "./ListLayout.module.less";
import { isNotNil, setTrackingId } from "client/Misc/utils";
import { flattenChildren } from "client/Misc/reactUtils";
import { LoadMoreIndicator } from "client/Components";
import classNames from "classnames";
import { ChildrenAndMoreButtonCollapseMode, useChildrenAndMoreButton } from "../useChildrenAndMoreButton";
import { never } from "client_legacy/ndaw/errors";

export type ListLayoutCollapseMode = {
	kind: "one-row" | "limited-items";
	isCollapsed: boolean;
	maxItemsCount: number;
};

export type ListLayoutProps = {
	trackingId: string;
	renderAfterItems?: ReactElement | null;
	hasBorderTop?: boolean;
	hasMobileSpacing?: boolean;
} & (
	{
		collapseMode?: undefined;
	}
	|
	{
		collapseMode: ListLayoutCollapseMode;
		renderMoreButton: ReactElement | null;
		shouldAlwaysRenderMoreButton?: boolean;
	}
);

export const ListLayout = (props: PropsWithChildren<ListLayoutProps>): ReactElement => {
	const {
		trackingId,
		children,
		renderAfterItems,
		hasBorderTop,
		hasMobileSpacing = false
	} = props;

	const childrenArray = useMemo(
		(): readonly ReactNode[] => {
			return flattenChildren(children);
		},
		[children]
	);

	const {
		childrenToRender,
		moreButtonToRender,
		containerRef,
		itemRef
	} = useChildrenAndMoreButton({
		childrenArray,
		itemGap: 16,
		...(
			props.collapseMode !== undefined
				? {
					collapseMode: convertCollapseMode(props.collapseMode),
					renderMoreButton: props.renderMoreButton,
					shouldAlwaysRenderMoreButton: props.shouldAlwaysRenderMoreButton
				}
				: {
					collapseMode: undefined
				}
		)
	});

	return (
		<>
			<div
				{...setTrackingId(trackingId)}
				className={styles.root}
			>
				<ul className={classNames(
					styles.list,
					{
						[styles.list_hasBorderTop]: hasBorderTop,
						[styles.list_hasMobileSpacing]: hasMobileSpacing
					}
				)}>
					{childrenToRender}
				</ul>
				{isNotNil(renderAfterItems) && (
					<div>
						{renderAfterItems}
					</div>
				)}
				<div className={styles.measurement}>
					<ul
						className={styles.list}
						ref={containerRef}
					>
						<li ref={itemRef} />
					</ul>
				</div>
			</div>
			{moreButtonToRender}
		</>
	);
};

ListLayout.displayName = "ListLayout";

const ListLayoutItem = (props: PropsWithChildren<{}>): ReactElement => {
	const {
		children
	} = props;

	return (
		<li>
			{children}
		</li>
	);
};

ListLayoutItem.displayName = "ListLayout.Item";

const ListLayoutMobilePlusMenuSpacing = (): ReactElement => {
	return (
		<div className="preserve-space-for-mobile-plus-button"/>
	);
};

ListLayoutMobilePlusMenuSpacing.displayName = "ListLayout.MobilePlusMenuSpacing";

ListLayout.Item = ListLayoutItem;
ListLayout.LoadMoreIndicator = LoadMoreIndicator;
ListLayout.MobilePlusMenuSpacing = ListLayoutMobilePlusMenuSpacing;

function convertCollapseMode(
	collapseMode: ListLayoutCollapseMode
): ChildrenAndMoreButtonCollapseMode {
	const {
		kind,
		isCollapsed,
		maxItemsCount
	} = collapseMode;

	switch (kind) {
		case "limited-items":
			return {
				kind: "limited-items",
				isCollapsed,
				maxItemsCount
			};
		case "one-row":
			return {
				kind: "limited-rows",
				isCollapsed,
				maxItemsCount,
				maxRowsCount: 1
			};
		default:
			never(kind);
	}
}